import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminBookForm from './AdminBookForm';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import AdminAiPromptModal from './AdminAiPromptModal';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AdminMediaLibrary from './AdminMediaLibrary';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const emptyChapter = () => ({ id: Date.now(), title: '', order: 1, content: '', sectionId: null });
const emptySection = () => ({ id: Date.now(), title: '', description: '', orderIndex: 0 });

// SortableChapterItem for drag handle
const SortableChapterItem = ({ chapter, index, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: chapter.id.toString() });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: isDragging ? '#e6e6ff' : undefined,
    boxShadow: isDragging ? '0 2px 8px #6c47ff33' : undefined
  };
  return (
    <li ref={setNodeRef} style={style} className="chapter-list-item" {...attributes}>
      <span className="drag-handle" {...listeners} style={{ cursor: 'grab', marginRight: 8 }}>
        <span className="material-icons">drag_indicator</span>
      </span>
      <span className="chapter-title">{chapter.title}</span>
      <div className="chapter-actions">
        <button type="button" onClick={() => onEdit(chapter)} title="Edit Chapter"><span className="material-icons">edit</span></button>
        <button type="button" onClick={() => onDelete(chapter.id)} title="Delete Chapter"><span className="material-icons">delete_forever</span></button>
      </div>
    </li>
  );
};

const NO_SECTION_ID = 'no-section';

const SortableSectionItem = ({ section, children }) => {
  return (
    <div className="section-container">
      <div className="section-header">
        <h4>{section.title}</h4>
        {section.description && <p className="section-description">{section.description}</p>}
      </div>
      <div>{children}</div>
    </div>
  );
};

const AdminBookManager = () => {
  const [book, setBook] = useState(null);
  const [sections, setSections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditBookOpen, setIsEditBookOpen] = useState(false);
  const [isEditChapterOpen, setIsEditChapterOpen] = useState(false);
  const [editingChapter, setEditingChapter] = useState(null);
  const [isAddChapterOpen, setIsAddChapterOpen] = useState(false);
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiChapterOptions, setAiChapterOptions] = useState([]);
  const [isOptionModalOpen, setIsOptionModalOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [fromAiOptions, setFromAiOptions] = useState(false);

  // Section management states
  const [isAddSectionOpen, setIsAddSectionOpen] = useState(false);
  const [isEditSectionOpen, setIsEditSectionOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionTitle, setSectionTitle] = useState('');
  const [sectionDescription, setSectionDescription] = useState('');

  // Book edit fields
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');

  // Chapter fields
  const [chapterTitle, setChapterTitle] = useState('');
  const [chapterContent, setChapterContent] = useState('');
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [chapterImageUrl, setChapterImageUrl] = useState('');
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);

  // Remove the comment moderation section and related state/hooks from this file.

  const sensors = useSensors(
    useSensor(PointerSensor)
  );

  const fetchBook = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('app_token');
      console.log('🔍 Fetching books from /api/admin/books');
      const res = await axios.get('/api/admin/books', { headers: { Authorization: `Bearer ${token}` } });
      console.log('📚 Books response:', res.data);
      
      if (res.data && res.data.length > 0) {
        let chapters = res.data[0].chapters;
        if (typeof chapters === 'string') {
          try { chapters = JSON.parse(chapters); } catch { chapters = []; }
        }
        console.log('📖 Parsed chapters:', chapters);
        setBook({ ...res.data[0], chapters: chapters || [] });
        
        // Fetch sections
        console.log('🔍 Fetching sections from /api/admin/books/' + res.data[0].id + '/sections');
        const sectionsRes = await axios.get(`/api/admin/books/${res.data[0].id}/sections`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        console.log('📑 Sections response:', sectionsRes.data);
        setSections(sectionsRes.data || []);
      } else {
        console.log('❌ No books found');
        setBook(null);
        setSections([]);
      }
    } catch (err) {
      console.error('❌ Error fetching book:', err);
      setError('Failed to fetch book');
    }
    setIsLoading(false);
  };

  useEffect(() => { fetchBook(); }, []);

  // Book edit modal logic
  const openEditBook = () => {
    setEditTitle(book.title);
    setEditDescription(book.description);
    setIsEditBookOpen(true);
  };
  const handleSaveBook = async () => {
    try {
      const token = localStorage.getItem('app_token');
      await axios.put(`/api/admin/books/${book.id}`, {
        title: editTitle,
        description: editDescription
      }, { headers: { Authorization: `Bearer ${token}` } });
      setIsEditBookOpen(false);
      fetchBook();
    } catch (err) {
      setError('Failed to update book');
    }
  };

  // Section management logic
  const openAddSection = () => {
    setSectionTitle('');
    setSectionDescription('');
    setEditingSection(null);
    setIsAddSectionOpen(true);
    setIsEditSectionOpen(false);
  };

  const openEditSection = (section) => {
    setSectionTitle(section.title);
    setSectionDescription(section.description || '');
    setEditingSection(section);
    setIsEditSectionOpen(true);
    setIsAddSectionOpen(false);
  };

  const handleSaveSection = async () => {
    try {
      const token = localStorage.getItem('app_token');
      if (editingSection) {
        await axios.put(`/api/admin/books/sections/${editingSection.id}`, {
          title: sectionTitle,
          description: sectionDescription
        }, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`/api/admin/books/${book.id}/sections`, {
          title: sectionTitle,
          description: sectionDescription,
          orderIndex: sections.length
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      setIsAddSectionOpen(false);
      setIsEditSectionOpen(false);
      setEditingSection(null);
      fetchBook();
    } catch (err) {
      setError('Failed to save section');
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Are you sure you want to delete this section? All chapters in this section will be moved to "No Section".')) {
      return;
    }
    try {
      const token = localStorage.getItem('app_token');
      await axios.delete(`/api/admin/books/sections/${sectionId}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      // Move chapters from deleted section to no section
      const updatedChapters = book.chapters.map(ch => 
        ch.sectionId === sectionId ? { ...ch, sectionId: null } : ch
      );
      await axios.put(`/api/admin/books/${book.id}`, {
        chapters: updatedChapters
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      fetchBook();
    } catch (err) {
      setError('Failed to delete section');
    }
  };

  // Chapter add/edit modal logic
  const openAddChapter = () => {
    setChapterTitle('');
    setChapterContent('');
    setSelectedSectionId(null);
    setChapterImageUrl('');
    setEditingChapter(null);
    setIsAddChapterOpen(true);
    setIsEditChapterOpen(false);
  };
  const openEditChapter = (ch) => {
    setChapterTitle(ch.title);
    setChapterContent(ch.content);
    setSelectedSectionId(ch.sectionId);
    setChapterImageUrl(ch.image_url || '');
    setEditingChapter(ch);
    setIsEditChapterOpen(true);
    setIsAddChapterOpen(false);
  };
  const handleSaveChapter = async () => {
    try {
      const token = localStorage.getItem('app_token');
      let chapters = book.chapters || [];
      if (editingChapter) {
        chapters = chapters.map(ch => ch.id === editingChapter.id ? 
          { ...ch, title: chapterTitle, content: chapterContent, sectionId: selectedSectionId, image_url: chapterImageUrl } : ch);
      } else {
        chapters = [...chapters, { 
          id: Date.now(), 
          title: chapterTitle, 
          order: chapters.length + 1, 
          content: chapterContent,
          sectionId: selectedSectionId,
          image_url: chapterImageUrl
        }];
      }
      const res = await axios.put(`/api/admin/books/${book.id}`, {
        chapters
      }, { headers: { Authorization: `Bearer ${token}` } });
      let updatedChapters = res.data.chapters;
      if (typeof updatedChapters === 'string') {
        try { updatedChapters = JSON.parse(updatedChapters); } catch { updatedChapters = []; }
      }
      setBook({ ...book, chapters: updatedChapters || [] });
      setIsAddChapterOpen(false);
      setIsEditChapterOpen(false);
      setEditingChapter(null);
      fetchBook();
    } catch (err) {
      setError('Failed to save chapter');
    }
  };
  const handleDeleteChapter = async (id) => {
    try {
      const token = localStorage.getItem('app_token');
      const chapters = (book.chapters || []).filter(ch => ch.id !== id);
      const res = await axios.put(`/api/admin/books/${book.id}`, { chapters }, { headers: { Authorization: `Bearer ${token}` } });
      let updatedChapters = res.data.chapters;
      if (typeof updatedChapters === 'string') {
        try { updatedChapters = JSON.parse(updatedChapters); } catch { updatedChapters = []; }
      }
      setBook({ ...book, chapters: updatedChapters || [] });
      fetchBook();
    } catch (err) {
      setError('Failed to delete chapter');
    }
  };

  // Handler for AI chapter generation
  const handleGenerateWithAi = async (prompt, provider) => {
    setIsGenerating(true);
    try {
      const token = localStorage.getItem('app_token');
      const res = await axios.post(`/api/admin/books/${book.id}/ai-chapter-from-raw-thought`, { rawThought: prompt, provider }, { headers: { Authorization: `Bearer ${token}` } });
      if (Array.isArray(res.data.options) && res.data.options.length > 0) {
        setAiChapterOptions(res.data.options);
        setIsOptionModalOpen(true);
      }
      setIsAiModalOpen(false);
    } catch (err) {
      setError('Failed to generate chapters with AI');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSelectChapterOption = (option) => {
    setSelectedOption(option);
    setFromAiOptions(true);
    setShowSectionSelect(true);
  };

  const handleBackToOptions = () => {
    setIsAddChapterOpen(false);
    setIsOptionModalOpen(true);
    setFromAiOptions(false);
  };

  // Group chapters by section
  const getChaptersBySection = () => {
    if (!book || !book.chapters) return { grouped: {}, noSectionChapters: [] };
    
    // Debug logging
    console.log('🔍 getChaptersBySection called with:');
    console.log('Book chapters:', book.chapters);
    console.log('Sections:', sections);
    console.log('Book chapters length:', book.chapters.length);
    console.log('Sections length:', sections.length);
    
    const grouped = {};
    const noSectionChapters = [];
    
    // First, create sections for any chapters that have section information
    book.chapters.forEach((chapter, index) => {
      console.log(`📖 Processing chapter ${index + 1}:`, {
        title: chapter.title,
        id: chapter.id,
        sectionId: chapter.sectionId,
        hasSectionId: !!chapter.sectionId,
        sectionsAvailable: sections.length
      });
      
      // Check if chapter has a valid sectionId that matches an existing section
      const hasValidSection = chapter.sectionId && sections.some(s => s.id == chapter.sectionId);
      
      console.log(`   Valid section check:`, {
        hasValidSection,
        sectionId: chapter.sectionId,
        matchingSections: sections.filter(s => s.id == chapter.sectionId)
      });
      
      if (hasValidSection) {
        const sectionId = chapter.sectionId.toString();
        if (!grouped[sectionId]) {
          grouped[sectionId] = [];
        }
        grouped[sectionId].push(chapter);
        console.log(`   ✅ Added to section ${sectionId}`);
      } else {
        noSectionChapters.push(chapter);
        console.log(`   ❌ Added to no section`);
      }
    });
    
    console.log('📊 Final grouping:', {
      grouped: Object.keys(grouped).map(key => ({ sectionId: key, chapters: grouped[key].length })),
      noSectionChapters: noSectionChapters.length,
      totalChapters: book.chapters.length
    });
    
    return { grouped, noSectionChapters };
  };

  // New states for section selection
  const [showSectionSelect, setShowSectionSelect] = useState(false);
  const [aiSectionId, setAiSectionId] = useState('');

  // New function to handle saving chapter from AI
  const handleSaveChapterFromAI = async (option, sectionId) => {
    try {
      const token = localStorage.getItem('app_token');
      await axios.post(`/api/admin/books/${book.id}/chapters`, {
        title: option.title,
        content: option.content,
        sectionId: sectionId || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchBook();
    } catch (err) {
      setError('Failed to save AI chapter');
    }
  };

  // Add state for section manager modal
  const [isSectionManagerOpen, setIsSectionManagerOpen] = useState(false);

  useEffect(() => {
    const modalOpen = isEditBookOpen || isAddSectionOpen || isEditSectionOpen || isAddChapterOpen || isEditChapterOpen || isAiModalOpen || isOptionModalOpen || isMediaLibraryOpen;
    if (modalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isEditBookOpen, isAddSectionOpen, isEditSectionOpen, isAddChapterOpen, isEditChapterOpen, isAiModalOpen, isOptionModalOpen, isMediaLibraryOpen]);

  if (isLoading) return <p>Loading book...</p>;
  if (error) return <p>{error}</p>;
  if (!book || !book.chapters) return <p>No book found. Please add a book in the database.</p>;

  const { grouped, noSectionChapters } = getChaptersBySection();
  
  // Debug logging for rendering
  console.log('🎯 Rendering with:', {
    sectionsCount: sections.length,
    chaptersCount: book.chapters.length,
    groupedChapters: Object.keys(grouped).length,
    noSectionChaptersCount: noSectionChapters.length,
    sections: sections,
    chapters: book.chapters
  });

  // Multi-container DnD state
  const sectionIds = sections.map(s => s.id.toString());
  const itemsBySection = {
    [NO_SECTION_ID]: noSectionChapters.map(ch => ch.id.toString()),
    ...Object.fromEntries(sections.map(s => [s.id.toString(), (grouped[s.id] || []).map(ch => ch.id.toString())]))
  };
  const chapterMap = Object.fromEntries((book.chapters || []).map(ch => [ch.id.toString(), ch]));
  
  console.log('📊 DnD state:', {
    sectionIds,
    itemsBySection,
    chapterMapKeys: Object.keys(chapterMap),
    totalChaptersInMap: Object.keys(chapterMap).length,
    sectionsWithChapters: Object.keys(itemsBySection).filter(key => itemsBySection[key].length > 0)
  });
  
  // Debug: Check if we have any chapters to display
  const totalChaptersToDisplay = Object.values(itemsBySection).reduce((sum, ids) => sum + ids.length, 0);
  console.log('🎯 Total chapters to display:', totalChaptersToDisplay);
  
  if (totalChaptersToDisplay === 0 && book.chapters.length > 0) {
    console.log('⚠️ WARNING: No chapters will be displayed despite having chapters in book!');
    console.log('Book chapters:', book.chapters);
    console.log('Items by section:', itemsBySection);
  }

  const handleBookDnDEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    // Section drag
    if (sectionIds.includes(active.id.toString()) && sectionIds.includes(over.id.toString())) {
      const oldIndex = sectionIds.indexOf(active.id.toString());
      const newIndex = sectionIds.indexOf(over.id.toString());
      const reorderedSections = arrayMove(sections, oldIndex, newIndex);
      setSections(reorderedSections);
      try {
        const token = localStorage.getItem('app_token');
        await axios.put(`/api/admin/books/${book.id}/sections/reorder`, {
          sectionIds: reorderedSections.map(s => s.id)
        }, { headers: { Authorization: `Bearer ${token}` } });
      } catch (err) {
        setError('Failed to save section order');
      }
      return;
    }
    // Chapter drag
    let sourceSection = null;
    let destSection = null;
    for (const secId of [NO_SECTION_ID, ...sectionIds]) {
      if (itemsBySection[secId].includes(active.id.toString())) sourceSection = secId;
      if (itemsBySection[secId].includes(over.id.toString())) destSection = secId;
    }
    if (!sourceSection || !destSection) return;
    let newItemsBySection = { ...itemsBySection };
    newItemsBySection[sourceSection] = newItemsBySection[sourceSection].filter(id => id !== active.id.toString());
    const overIndex = newItemsBySection[destSection].indexOf(over.id.toString());
    newItemsBySection[destSection] = [
      ...newItemsBySection[destSection].slice(0, overIndex),
      active.id.toString(),
      ...newItemsBySection[destSection].slice(overIndex)
    ];
    // Build new chapters array
    let newChapters = [];
    for (const secId of [NO_SECTION_ID, ...sectionIds]) {
      newItemsBySection[secId].forEach((id, idx) => {
        const ch = { ...chapterMap[id] };
        ch.order = newChapters.length + 1;
        ch.sectionId = secId === NO_SECTION_ID ? null : secId;
        newChapters.push(ch);
      });
    }
    setBook({ ...book, chapters: newChapters });
    try {
      const token = localStorage.getItem('app_token');
      await axios.put(`/api/admin/books/${book.id}`, { chapters: newChapters }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) {
      setError('Failed to save chapter order');
    }
  };

  // Add a handler for section drag-and-drop in the modal
  const handleSectionDnDEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sections.findIndex(s => s.id.toString() === active.id.toString());
    const newIndex = sections.findIndex(s => s.id.toString() === over.id.toString());
    const reorderedSections = arrayMove(sections, oldIndex, newIndex);
    setSections(reorderedSections);
    try {
      const token = localStorage.getItem('app_token');
      await axios.put(`/api/admin/books/${book.id}/sections/reorder`, {
        sectionIds: reorderedSections.map(s => s.id)
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) {
      setError('Failed to save section order');
    }
  };

  return (
    <div className="admin-content-manager">
      <div className="admin-content-header">
        <h3 className="admin-section-title">Book: {book.title}</h3>
        <div className="admin-top-buttons-scroll" style={{ display: 'flex', gap: '1rem', overflowX: 'auto', paddingBottom: 8 }}>
          <button onClick={openEditBook} className="btn-primary">Edit Book</button>
          <button onClick={openAddChapter} className="btn-primary">Add New Chapter</button>
          <button onClick={() => setIsAiModalOpen(true)} className="btn-primary" style={{ backgroundColor: '#6c47ff' }}>Create Chapter with AI</button>
          <button onClick={() => setIsSectionManagerOpen(true)} className="btn-primary">Manage Sections</button>
        </div>
      </div>
      {sections.length === 0 && book.chapters.length > 0 ? (
        // Fallback: Show chapters directly if no sections
        <div className="admin-content-body">
          <div className="section-container">
            <div className="section-header">
              <h4>All Chapters (No Sections Defined)</h4>
              <p>Use "Manage Sections" to organize chapters into sections.</p>
            </div>
            <ul className="chapter-list">
              {book.chapters.map((chapter, i) => (
                <SortableChapterItem
                  key={chapter.id}
                  chapter={chapter}
                  index={i}
                  onEdit={openEditChapter}
                  onDelete={handleDeleteChapter}
                />
              ))}
            </ul>
          </div>
        </div>
      ) : sections.length > 0 && totalChaptersToDisplay === 0 ? (
        // Fallback: Show all chapters if sections exist but no chapters are being displayed
        <div className="admin-content-body">
          <div className="section-container">
            <div className="section-header">
              <h4>All Chapters (Section Assignment Issue)</h4>
              <p>Chapters are not properly assigned to sections. Showing all chapters here.</p>
            </div>
            <ul className="chapter-list">
              {book.chapters.map((chapter, i) => (
                <SortableChapterItem
                  key={chapter.id}
                  chapter={chapter}
                  index={i}
                  onEdit={openEditChapter}
                  onDelete={handleDeleteChapter}
                />
              ))}
            </ul>
          </div>
        </div>
      ) : sections.length > 0 ? (
        // Normal rendering with sections
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleBookDnDEnd}>
          <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
            {sections.map((section) => (
              <SortableSectionItem
                key={section.id}
                section={section}
              >
                <SortableContext items={itemsBySection[section.id.toString()] || []} strategy={verticalListSortingStrategy}>
                  <ul className="chapter-list">
                    {(itemsBySection[section.id.toString()] || []).map((id, i) => (
                      <SortableChapterItem
                        key={id}
                        chapter={chapterMap[id]}
                        index={i}
                        onEdit={openEditChapter}
                        onDelete={handleDeleteChapter}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </SortableSectionItem>
            ))}
            {/* No Section container */}
            {noSectionChapters.length > 0 && (
              <div className="section-container">
                <div className="section-header">
                  <h4>No Section</h4>
                </div>
                <SortableContext items={itemsBySection[NO_SECTION_ID] || []} strategy={verticalListSortingStrategy}>
                  <ul className="chapter-list">
                    {(itemsBySection[NO_SECTION_ID] || []).map((id, i) => (
                      <SortableChapterItem
                        key={id}
                        chapter={chapterMap[id]}
                        index={i}
                        onEdit={openEditChapter}
                        onDelete={handleDeleteChapter}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </div>
            )}
          </SortableContext>
        </DndContext>
      ) : (
        // Fallback: Show all chapters if no sections and complex logic fails
        <div className="admin-content-body">
          <div className="section-container">
            <div className="section-header">
              <h4>All Chapters</h4>
              <p>No sections defined. All chapters are shown here.</p>
            </div>
            <ul className="chapter-list">
              {book.chapters.map((chapter, i) => (
                <SortableChapterItem
                  key={chapter.id}
                  chapter={chapter}
                  index={i}
                  onEdit={openEditChapter}
                  onDelete={handleDeleteChapter}
                />
              ))}
            </ul>
          </div>
        </div>
      )}
      {/* Edit Book Modal */}
      {isEditBookOpen && (
        <div className="modal-overlay" onClick={() => {
          if (editTitle || editDescription) {
            if (window.confirm('You have unsaved changes. Close anyway?')) {
              setIsEditBookOpen(false);
            }
          } else {
            setIsEditBookOpen(false);
          }
        }}>
          <div className="modal modern-book-modal" style={{ maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <h3>Edit Book</h3>
            <div className="modern-book-form">
              <label>Title</label>
              <input value={editTitle} onChange={e => setEditTitle(e.target.value)} required />
              <label>Description</label>
              <textarea value={editDescription} onChange={e => setEditDescription(e.target.value)} rows={3} />
              <div className="form-actions">
                <button className="btn-secondary" onClick={() => setIsEditBookOpen(false)}>Cancel</button>
                <button className="btn-primary" onClick={handleSaveBook}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Add/Edit Section Modal */}
      {(isAddSectionOpen || isEditSectionOpen) && (
        <div className="modal-overlay" onClick={() => {
          if (sectionTitle || sectionDescription) {
            if (window.confirm('You have unsaved changes. Close anyway?')) {
              setIsAddSectionOpen(false);
              setIsEditSectionOpen(false);
              setEditingSection(null);
            }
          } else {
            setIsAddSectionOpen(false);
            setIsEditSectionOpen(false);
            setEditingSection(null);
          }
        }}>
          <div className="modal modern-book-modal" style={{ maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <h3>{editingSection ? 'Edit Section' : 'Add New Section'}</h3>
            <div className="modern-book-form">
              <label>Section Title</label>
              <input 
                value={sectionTitle} 
                onChange={e => setSectionTitle(e.target.value)} 
                placeholder="Enter section title"
                required 
              />
              <label>Description (Optional)</label>
              <textarea 
                value={sectionDescription} 
                onChange={e => setSectionDescription(e.target.value)} 
                placeholder="Enter section description"
                rows={3} 
              />
              <div className="form-actions">
                <button className="btn-secondary" onClick={() => {
                  setIsAddSectionOpen(false);
                  setIsEditSectionOpen(false);
                  setEditingSection(null);
                }}>Cancel</button>
                <button className="btn-primary" onClick={handleSaveSection}>
                  {editingSection ? 'Update' : 'Add'} Section
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Chapter Modal */}
      {(isAddChapterOpen || isEditChapterOpen) && (
        <div className="modal-overlay" onClick={() => {
          if (chapterTitle || chapterContent || selectedSectionId || chapterImageUrl) {
            if (window.confirm('You have unsaved changes. Close anyway?')) {
              setIsAddChapterOpen(false);
              setIsEditChapterOpen(false);
              setEditingChapter(null);
            }
          } else {
            setIsAddChapterOpen(false);
            setIsEditChapterOpen(false);
            setEditingChapter(null);
          }
        }}>
          <div className="modal modern-book-modal" style={{ maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <h3>{editingChapter ? 'Edit Chapter' : 'Add New Chapter'}</h3>
            <div className="modern-book-form">
              <label>Chapter Title</label>
              <input 
                value={chapterTitle} 
                onChange={e => setChapterTitle(e.target.value)} 
                placeholder="Enter chapter title"
                required 
              />
              <label>Section</label>
              <select 
                value={selectedSectionId || ''} 
                onChange={e => setSelectedSectionId(e.target.value || null)}
              >
                <option value="">No Section</option>
                {sections.map(section => (
                  <option key={section.id} value={section.id}>{section.title}</option>
                ))}
              </select>
              <label>Chapter Image</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                {chapterImageUrl && (
                  <div style={{ position: 'relative' }}>
                    <img src={chapterImageUrl} alt="Chapter" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6, border: '1px solid #ccc' }} />
                    <button type="button" style={{ position: 'absolute', top: -8, right: -8, background: '#fff', border: '1px solid #ccc', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', fontSize: 14 }} onClick={() => setChapterImageUrl('')} title="Remove Image">&times;</button>
                  </div>
                )}
                <button type="button" className="btn-secondary" onClick={() => setIsMediaLibraryOpen(true)}>
                  {chapterImageUrl ? 'Change Image' : 'Select Image'}
                </button>
              </div>
              <label>Chapter Content</label>
              <CKEditor
                editor={ClassicEditor}
                data={chapterContent}
                onChange={(event, editor) => setChapterContent(editor.getData())}
              />
              <div className="form-actions">
                <button className="btn-secondary" onClick={() => {
                  setIsAddChapterOpen(false);
                  setIsEditChapterOpen(false);
                  setEditingChapter(null);
                }}>Cancel</button>
                <button className="btn-primary" onClick={handleSaveChapter}>
                  {editingChapter ? 'Update' : 'Add'} Chapter
                </button>
                {fromAiOptions && (
                  <button type="button" className="btn-secondary" style={{ marginLeft: 8 }} onClick={handleBackToOptions}>
                    Back to Options
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {isAiModalOpen && (
        <AdminAiPromptModal
          onGenerate={handleGenerateWithAi}
          onCancel={() => setIsAiModalOpen(false)}
          isGenerating={isGenerating}
        />
      )}
      {isOptionModalOpen && (
        <div className="modal-overlay" onClick={() => setIsOptionModalOpen(false)}>
          <div className="modal floating-modal" style={{ maxHeight: '90vh', overflowY: 'auto', minWidth: 500 }} onClick={e => e.stopPropagation()}>
            <h3>Select an AI-Generated Chapter Option</h3>
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              {aiChapterOptions.map((option, idx) => (
                <div key={idx} className="ai-article-option" style={{ border: '1px solid #ccc', borderRadius: 8, padding: 12, marginBottom: 12, cursor: 'pointer', background: selectedOption === option ? '#f0f8ff' : '#fff' }} onClick={() => handleSelectChapterOption(option)}>
                  <strong>{option.title}</strong>
                  <div style={{ margin: '8px 0', color: '#666', fontSize: 14 }}>{option.content?.slice(0, 120) + (option.content?.length > 120 ? '...' : '')}</div>
                </div>
              ))}
            </div>
            <button className="btn-secondary" onClick={() => setIsOptionModalOpen(false)} style={{ marginTop: 16 }}>Cancel</button>
          </div>
        </div>
      )}
      {isMediaLibraryOpen && (
        <AdminMediaLibrary
          s3Prefix="hj1/public/assets/images/"
          onSelect={url => { setChapterImageUrl(url); setIsMediaLibraryOpen(false); }}
          onCancel={() => setIsMediaLibraryOpen(false)}
        />
      )}
      {showSectionSelect && (
        <div className="modal-overlay" onClick={() => setShowSectionSelect(false)}>
          <div className="modal modern-book-modal" style={{ maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <h3>Assign Section to New Chapter</h3>
            <select value={aiSectionId} onChange={e => setAiSectionId(e.target.value)}>
              <option value="">No Section</option>
              {sections.map(section => (
                <option key={section.id} value={section.id}>{section.title}</option>
              ))}
            </select>
            <div className="form-actions">
              <button className="btn-secondary" onClick={() => setShowSectionSelect(false)}>Cancel</button>
              <button className="btn-primary" onClick={async () => {
                await handleSaveChapterFromAI(selectedOption, aiSectionId);
                setShowSectionSelect(false);
                setIsOptionModalOpen(false);
              }}>Save Chapter</button>
            </div>
          </div>
        </div>
      )}
      {isSectionManagerOpen && (
        <div className="modal-overlay" onClick={() => setIsSectionManagerOpen(false)}>
          <div className="modal modern-book-modal" onClick={e => e.stopPropagation()} style={{ minWidth: 400, maxWidth: 600 }}>
            <h3>Manage Sections</h3>
            <p style={{ color: '#888', fontSize: 14, marginBottom: 12 }}>Drag to reorder, edit name/description, or delete sections.</p>
            <button onClick={openAddSection} className="btn-primary" style={{ marginBottom: 12 }}>Add Section</button>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionDnDEnd}>
              <SortableContext items={sections.map(s => s.id.toString())} strategy={verticalListSortingStrategy}>
                {sections.map((section) => (
                  <SortableSectionRow key={section.id} section={section} onEdit={openEditSection} onDelete={handleDeleteSection} />
                ))}
              </SortableContext>
            </DndContext>
            <div className="form-actions">
              <button className="btn-secondary" onClick={() => setIsSectionManagerOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
      <style>{`
        .book-sections { margin-top: 20px; }
        .section-container { 
          margin-bottom: 20px; 
          border: 1px solid #eee; 
          border-radius: 8px; 
          overflow: hidden; 
        }
        .section-header { 
          display: flex; 
          justify-content: space-between; 
          align-items: center; 
          padding: 15px; 
          background: #f8f9fa; 
          border-bottom: 1px solid #eee;
        }
        .section-header h4 { margin: 0; }
        .section-description { 
          margin: 5px 0 0 0; 
          font-size: 14px; 
          color: #666; 
          flex: 1;
          margin-left: 15px;
        }
        .section-actions { display: flex; gap: 8px; }
        .section-actions button { padding: 4px 8px; font-size: 12px; }
        .modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.4);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow-y: auto;
        }
        .modal {
          background: #fff;
          border-radius: 10px;
          padding: 24px;
          max-width: 95vw;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 4px 32px rgba(0,0,0,0.18);
          position: relative;
        }
      `}</style>
    </div>
  );
};

const SortableSectionRow = ({ section, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id.toString() });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    background: isDragging ? '#e6e6ff' : '#f8f8ff',
    borderRadius: 6,
    padding: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8
  };
  return (
    <div ref={setNodeRef} style={style}>
      <span className="drag-handle" {...attributes} {...listeners} style={{ cursor: 'grab', marginRight: 8 }}>
        <span className="material-icons">drag_indicator</span>
      </span>
      <span style={{ flex: 1 }}>
        <strong>{section.title}</strong>
        {section.description && <span style={{ marginLeft: 8, color: '#888' }}>{section.description}</span>}
      </span>
      <button className="btn-secondary" onClick={() => onEdit(section)}>Edit</button>
      <button className="btn-secondary" onClick={() => onDelete(section.id)} style={{ marginLeft: 8 }}>Delete</button>
    </div>
  );
};

export default AdminBookManager; 